import * as THREE from "three";
import { preloadAssets } from "./assets";
import { buildGrass } from "./world-grass";
import { VERT_DIM, MAP_UNITS, HEIGHT_VISUAL_SCALE } from "./world/constants";
import type { ObjectRow, RegionRow, TeamSpawns, World } from "./world/types";
import { smoothHeightsForVisuals, exaggerateCliffHeights, buildTerrain } from "./world/terrain";
import { buildObjects, bakeTerrainContactAO, buildBorderTrees } from "./world/doodads";
import { buildScatterDetail, buildCliffLipVegetation } from "./world/scatter-detail";
import { buildCanyonRocks } from "./world/canyon-rocks";
import { buildRegions } from "./world/markers";
import { buildHomeFires } from "./world/home-fire";
import { toToon } from "./toon";
import { resolveDoodadPlacement, filterDoodadsOnFlatGround, repairSealedCorridors, collectStaticObstacles, buildObstacleField, VOLCANO_LANDMARK } from "@tribez/shared";
import type { AssetBundle } from "./assets";

// Central map landmark anchor — SINGLE source of truth lives in
// shared/obstacles.ts (VOLCANO_LANDMARK: position + collision circle), so the
// visual, the collision field, and the navmesh can never disagree again. The
// old local consts here kept pre-flip coordinates after the 2026-07-05 N-S map
// flip, stranding the volcano mid-map with its "buried" pink base tile
// surfacing (Aviv's pink-surface report, 2026-07-06).
const VOLCANO_X = VOLCANO_LANDMARK.x;
const VOLCANO_Z = VOLCANO_LANDMARK.z;

/**
 * Volcano landmark. The RedWorks Terrain_Volcano is ONE fused mesh — a 200u flat
 * tile (pink ground + water moat) with a central ~73u cone. We scale it down and
 * SINK the flat tile below the terrain surface so only the cone pokes up; the
 * opaque terrain mesh occludes the buried tile/moat. Toon-shaded to match the world.
 */
function buildVolcanoLandmark(assets: AssetBundle, sampleHeight: (x: number, z: number) => number): THREE.Object3D | null {
  if (!assets.volcano) return null;
  const v = assets.volcano.clone(true);
  v.traverse((node) => {
    const m = node as THREE.Mesh;
    if (!m.isMesh) return;
    m.castShadow = true; m.receiveShadow = true;
    const conv = (src: THREE.Material) => toToon(src);
    m.material = Array.isArray(m.material) ? m.material.map(conv) : conv(m.material);
  });
  const SCALE = 0.25;     // 73u cone → ~18m tall
  const SINK = 3.0;       // bury the flat tile below terrain so only the cone shows
  v.scale.setScalar(SCALE);
  v.position.set(VOLCANO_X, sampleHeight(VOLCANO_X, VOLCANO_Z) - SINK, VOLCANO_Z);
  return v;
}

// Public surface — re-exported so `import { ... } from "./world"` keeps working
// after the world/ split. main.ts imports loadWorld, MAP_UNITS, World.
export { VERT_DIM, MAP_UNITS, HEIGHT_VISUAL_SCALE };
export type { World };
// NatureKey was historically re-exported from this module's import surface.
export type { NatureKey } from "./assets";

const fetchJson = async <T>(path: string): Promise<T> => {
  const res = await fetch(path);
  if (!res.ok) throw new Error(`fetch ${path} failed: ${res.status}`);
  return res.json() as Promise<T>;
};

const fetchBinary = async (path: string): Promise<ArrayBuffer> => {
  const res = await fetch(path);
  if (!res.ok) throw new Error(`fetch ${path} failed: ${res.status}`);
  return res.arrayBuffer();
};

export async function loadWorld(scene: THREE.Scene): Promise<World> {
  const [heightBuf, cliffBuf, pathingBuf, rawObjects, regions, spawns, assets] = await Promise.all([
    fetchBinary("/world/heightmap.bin"),
    fetchBinary("/world/cliffs.bin"),
    fetchBinary("/world/pathing.bin"),
    fetchJson<ObjectRow[]>("/world/objects.json"),
    fetchJson<RegionRow[]>("/world/regions.json"),
    fetchJson<TeamSpawns>("/world/team-spawns.json"),
    preloadAssets(),
  ]);

  const heights = new Float32Array(heightBuf);
  if (heights.length !== VERT_DIM * VERT_DIM) {
    throw new Error(`heightmap size mismatch: ${heights.length} != ${VERT_DIM * VERT_DIM}`);
  }
  const cliffLevels = new Uint16Array(cliffBuf);

  // Nudge decorative rocks out of trees / harvestables / each other AND out of
  // narrow ramp corridors (no deletion, so density is preserved), THEN drop any
  // doodad whose base would float over an exaggerated cliff step
  // (filterDoodadsOnFlatGround), THEN repair doodads whose COLLISION circles
  // seal a KTS-walkable corridor (repairSealedCorridors — drops the sealing
  // rocks/tree-trunks; rocks preferred). All passes are deterministic + shared
  // with the server collision (static-obstacles.ts) and navmesh (navmesh.ts),
  // so rendered == collided == pathed.
  const objects = repairSealedCorridors(
    filterDoodadsOnFlatGround(
      resolveDoodadPlacement(rawObjects, MAP_UNITS, cliffLevels),
      cliffLevels,
    ),
    { pathing: new Uint8Array(pathingBuf), heights, regions, cliffLevels },
  );

  // Visual heights: a once-smoothed copy used for the terrain mesh AND for every
  // visual Y placement (doodads, region rings, spawn pillars, player Y lerp,
  // creatures, buildings). The raw `heights` buffer is exposed on the World for
  // gameplay-side or server-mirrored math; the server has its own raw copy and
  // is unaffected. Smoothing both the mesh and its consumers keeps the visual
  // layer internally consistent so nothing floats/sinks against the ground.
  // exaggerateCliffHeights lifts the higher cliff levels so cliffs read as KTS-style
  // walls (visual only — navmesh/collision stay on the raw server heights). The blur
  // runs AFTER, so it both de-facets the 1m grid AND rounds the exaggeration's small
  // per-cell ramp steps — keeping only the hard plateau↔plateau cliff lips crisp.
  const visualHeights = smoothHeightsForVisuals(
    exaggerateCliffHeights(heights, cliffLevels),
    cliffLevels,
  );

  const sampleHeight = (x: number, z: number): number => {
    if (x < 0 || x >= MAP_UNITS || z < 0 || z >= MAP_UNITS) return 0;
    const col = Math.floor(x);
    const row = Math.floor(z);
    const fx = x - col;
    const fz = z - row;
    const i = row * VERT_DIM + col;
    const h00 = visualHeights[i]!;
    const h10 = visualHeights[i + 1]!;
    const h01 = visualHeights[i + VERT_DIM]!;
    const h11 = visualHeights[i + VERT_DIM + 1]!;
    const a = h00 * (1 - fx) + h10 * fx;
    const b = h01 * (1 - fx) + h11 * fx;
    return (a * (1 - fz) + b * fz) * HEIGHT_VISUAL_SCALE;
  };

  const terrain = buildTerrain(visualHeights, spawns);
  scene.add(terrain);
  // Cliffs/hillsides now read entirely from the terrain itself: steep exaggerated
  // elevation + a slope-driven earthy-brown that feathers into the grass (terrain.ts
  // BIOME_ROCK / CLIFF_SLOPE_BAND). The draped Cliff_Formation doodads were dropped —
  // they read as sparse pasted rocks, not a continuous hillside (Aviv, 2026-06-30).
  const objectsRoot = buildObjects(objects, sampleHeight, assets);
  scene.add(objectsRoot);
  // Border tree-line over the terrain skirt so the forest continues past the
  // play edge and the boundary disappears into the fog (issue #4).
  scene.add(buildBorderTrees(sampleHeight, assets));
  const volcano = buildVolcanoLandmark(assets, sampleHeight);
  if (volcano) scene.add(volcano);
  // Bake contact-AO halos into the terrain colors around each static doodad.
  // Pairs with `bakeBaseAO` on the doodad meshes themselves — together they
  // read as proper grounded shadow without SSAO or per-doodad blob decals.
  bakeTerrainContactAO(terrain, objects);
  scene.add(buildRegions(regions, sampleHeight));

  const grass = buildGrass(sampleHeight, assets);
  scene.add(grass.group);
  console.log(`[grass] placed ${grass.count} tufts across chunked InstancedMeshes`);

  // Stone-age re-theme detail passes (procedural, deterministic):
  //  - forest-floor scatter: mushrooms / ferns / rare bones
  //  - canyon framing: cliff/canyon-wall + rock clusters around the dry water (Fish) regions
  buildScatterDetail(scene, assets, sampleHeight, MAP_UNITS);
  // Cliff-lip vegetation (Bet 3 slice ②): drape ferns/bushes over steep-face
  // top edges so hills read organic like KTS. Client-only decoration keyed off
  // the shared cliff-edge extractor (raw heights classify the walls; sampleHeight
  // grounds each plant on the visual plateau top). No collision / navmesh impact.
  buildCliffLipVegetation(scene, assets, sampleHeight, cliffLevels, heights);
  // Canyon rocks are added as children of the doodad "objects" root (not a
  // separate scene-level group) so they automatically participate in
  // whatever already walks/raycasts that root — the whole-canopy fade scan
  // and the x-ray silhouette occluder list (both keyed off main.ts's
  // `doodadObjectsRoot = scene.getObjectByName("objects")`) — the same way
  // forest doodads do, with no separate wiring (bug fix 2026-07-02).
  buildCanyonRocks(objectsRoot, assets, sampleHeight, regions);

  // Static collision field — same shared geometry the server uses. Currently
  // unconsumed at runtime (the movement predictors that ate it were deleted in
  // the 2026-07-08 interpolation revert); kept because it's the client's only
  // ready-made mirror of the server obstacle set, useful for debug overlays.
  const obstacles = buildObstacleField(collectStaticObstacles(objects, regions));

  // Sacred bonfire landmarks — one per team spawn. Standing-stone ring + tribe-
  // tinted flame + ground-ring boundary cue + vertical beacon column. Teaches
  // new players "this is my safe home base" without text. update() drives flame
  // animation and must be called each frame (see main.ts render loop).
  const homeFires = buildHomeFires(spawns, sampleHeight);
  scene.add(homeFires.group);

  return { heights, sampleHeight, spawns, terrain, assets, grass, obstacles, homeFires };
}
